import React, { useState } from 'react';
import { Typography, theme } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import { IconChevronDown } from '@tabler/icons-react';
import { SURFACE_1_SOFT } from '../theme/creamyTokens';

const { Title, Text } = Typography;

/**
 * A card that splits one total into its parts: a label, a share bar and an
 * amount per row -- and, where a part is made of smaller parts, a second level
 * under it.
 *
 * Written for "por máquina" and then wanted verbatim for "de qué sale el
 * dinero" -- same shape, same bar, same right-hand column, different rows. So
 * it is one component with rows passed in, rather than the second block being
 * a copy of the first that drifts the first time either is touched.
 *
 * The bar is the point: which machine (or which accessory) carries the month
 * is a comparison, and a comparison is easier to see than to read.
 */
export interface IEarningsBreakdownRow {
  key: string;
  icon: React.ReactNode;
  label: string;
  /** Under the amount, on the right -- a count, a percentage, whatever the row is worth saying twice. */
  caption?: React.ReactNode;
  /** Already formatted: this component never decides how a number reads. */
  amount: React.ReactNode;
  /** 0-100. The share of the whole this row accounts for. */
  share: number;
  /**
   * What this row is made of. A row with children becomes a toggle; one
   * without stays a plain row, so a concept with nothing inside it never grows
   * a control that opens onto nothing.
   *
   * Children carry their own `share`, which callers compute against their
   * PARENT rather than against the card's total: inside a group, the useful
   * comparison is between siblings.
   */
  children?: IEarningsBreakdownRow[];
}

export interface IEarningsBreakdownCardProps {
  title: string;
  subtitle?: string;
  rows: IEarningsBreakdownRow[];
  style?: React.CSSProperties;
}

/** 0-100, and never a bar growing the wrong way when a refund inverts a row. */
const clampShare = (share: number) => Math.max(0, Math.min(100, share));

const ShareBar: React.FC<{ share: number; height: number; color: string; track: string }> = ({
  share,
  height,
  color,
  track,
}) => (
  <div style={{ height, borderRadius: 999, background: track, marginTop: 6 }}>
    <div style={{ height, borderRadius: 999, width: `${clampShare(share)}%`, background: color }} />
  </div>
);

const EarningsBreakdownCard: React.FC<IEarningsBreakdownCardProps> = ({ title, subtitle, rows, style }) => {
  const { token } = theme.useToken();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  if (rows.length === 0) return null;

  return (
    <div
      style={{
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: 16,
        padding: 16,
        height: '100%',
        ...style,
      }}
    >
      <Title level={5} style={{ margin: '0 0 4px' }}>
        {title}
      </Title>
      {subtitle && (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {subtitle}
        </Text>
      )}

      {rows.map((row, index) => {
        const children = row.children ?? [];
        const isOpen = !!expanded[row.key];

        return (
          <div
            key={row.key}
            style={{
              marginTop: 14,
              paddingTop: index === 0 ? 0 : 14,
              borderTop: index === 0 ? undefined : `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            {/* A real button when there is something to open, a plain div when
                there is not: a row that looks pressable and does nothing is
                worse than one that never looked it. */}
            <div
              role={children.length > 0 ? 'button' : undefined}
              tabIndex={children.length > 0 ? 0 : undefined}
              aria-expanded={children.length > 0 ? isOpen : undefined}
              onClick={children.length > 0 ? () => setExpanded((prev) => ({ ...prev, [row.key]: !prev[row.key] })) : undefined}
              onKeyDown={
                children.length > 0
                  ? (event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      setExpanded((prev) => ({ ...prev, [row.key]: !prev[row.key] }));
                    }
                  : undefined
              }
              style={{
                display: 'flex',
                gap: 12,
                alignItems: 'center',
                cursor: children.length > 0 ? 'pointer' : undefined,
              }}
            >
              <span
                style={{
                  display: 'flex',
                  flexShrink: 0,
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: SURFACE_1_SOFT,
                  color: token.colorTextSecondary,
                }}
              >
                {row.icon}
              </span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Text strong style={{ fontSize: 14, minWidth: 0 }} ellipsis>
                    {row.label}
                  </Text>
                  {children.length > 0 && (
                    <motion.span
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.18, ease: 'easeOut' }}
                      style={{ display: 'flex', flexShrink: 0, color: token.colorTextTertiary }}
                    >
                      <IconChevronDown size={15} />
                    </motion.span>
                  )}
                </div>
                <ShareBar share={row.share} height={6} color={token.colorPrimary} track={token.colorFillQuaternary} />
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <Text strong style={{ fontSize: 14, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  {row.amount}
                </Text>
                {row.caption && (
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                    {row.caption}
                  </Text>
                )}
              </div>
            </div>

            <AnimatePresence initial={false}>
              {isOpen && children.length > 0 && (
                <motion.div
                  key="children"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.22, ease: 'easeOut' }}
                  style={{ overflow: 'hidden' }}
                >
                  {/* Indented under the icon column, so the second level reads
                      as belonging to the row above it rather than as more rows
                      of the same list. */}
                  <div style={{ paddingLeft: 48, paddingTop: 12 }}>
                    {children.map((child) => (
                      <div key={child.key} style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 10 }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <Text style={{ fontSize: 13, display: 'block' }} ellipsis>
                            {child.label}
                          </Text>
                          <ShareBar
                            share={child.share}
                            height={4}
                            color={token.colorPrimaryBorder}
                            track={token.colorFillQuaternary}
                          />
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <Text style={{ fontSize: 13, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                            {child.amount}
                          </Text>
                          {child.caption && (
                            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                              {child.caption}
                            </Text>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

export default EarningsBreakdownCard;
